import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PaymentModal from './PaymentModal';
import { BtcIcon, EthIcon, SolIcon } from './icons/PricingIcons';
import { ATLAS_PASS_PLAN, DEFAULT_PRICING_PLANS } from '../utils/pricingPlans';
import './CrackerController.css';
import './Auth.css';
import './Payment.css';
import './Pricing.atlas.css';
import './AuthGate.css';

interface PlanFeature {
  name: string;
  head?: boolean;
}

const ATLAS_FEATURES: PlanFeature[] = [
  { name: 'Workspace', head: true },
  { name: 'Private Atlas lane — dedicated scanner instance' },
  { name: 'Unlimited URLs, paths, and threads' },
  { name: 'Multi-device panel · 24/7 access' },
  { name: 'Surface', head: true },
  { name: 'AWS · SMTP · API · Wallet · DB recon' },
  { name: '628+ path probes · RCE payloads · CVE 2024–2026' },
  { name: 'K8s · Git dumper · JS crawler · Laravel · MISCONFIG' },
  { name: 'Cloudflare & WAF bypass (6 methods)' },
  { name: 'Hardware', head: true },
  { name: 'Ryzen 9 · 16 cores · 48 GB · 10 Gbps private' },
  { name: 'Billing', head: true },
  { name: 'Single flat pass — no tier maze' },
  { name: 'Crypto checkout · no KYC · no logs' },
];

interface CheckoutPlan {
  name: string;
  price: string;
  period: string;
  description: string;
  cryptoPrice: { BTC: number; ETH: number; SOL: number };
  planId: string;
}

const buildCheckoutPlan = (): CheckoutPlan => ({
  name: ATLAS_PASS_PLAN.name,
  price: ATLAS_PASS_PLAN.price,
  period: ATLAS_PASS_PLAN.period,
  description: ATLAS_PASS_PLAN.description ?? '',
  cryptoPrice: ATLAS_PASS_PLAN.cryptoPrice,
  planId: ATLAS_PASS_PLAN.planId,
});

const Pricing = (): React.ReactElement => {
  const [plan, setPlan] = useState<CheckoutPlan>(buildCheckoutPlan);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const response = await fetch('https://glitchcloud.app/license-api/pricing');
        if (response.ok) {
          const data = await response.json();
          const match =
            data.plans?.find((p: { planId?: string; price?: string }) => p.planId === 'starter-monthly') ??
            data.plans?.find((p: { price?: string }) => String(p.price).includes('600'));
          if (match) {
            setPlan({
              name: 'Atlas Pass',
              price: match.price ?? '600€',
              period: match.period ?? ATLAS_PASS_PLAN.period,
              description: match.description ?? ATLAS_PASS_PLAN.description ?? '',
              cryptoPrice: match.cryptoPrice ?? ATLAS_PASS_PLAN.cryptoPrice,
              planId: match.planId ?? 'starter-monthly',
            });
          }
        }
      } catch (error) {
        console.error('Pricing load failed:', error);
        setPlan(buildCheckoutPlan());
      } finally {
        setIsLoading(false);
      }
    };
    loadPricing();
  }, []);

  if (isLoading) {
    return (
      <div className="auth-shell auth-grid-bg pricing-loading">
        <div className="auth-scanline absolute inset-0 pointer-events-none" />
        <div className="pricing-loading-spinner" />
        <p className="cracker-label">Loading offer…</p>
      </div>
    );
  }

  const handlePurchase = () => setShowPaymentModal(true);

  return (
    <div className="auth-gate auth-gate--pricing pricing-atlas-page relative overflow-hidden">
      <div className="auth-scanline absolute inset-0 pointer-events-none" />
      <div className="auth-top-line" />

      <div className="relative z-10 pricing-atlas-grid">
        <button type="button" onClick={() => navigate('/')} className="auth-back md:col-span-2">
          <span className="auth-back-icon" aria-hidden="true">←</span>
          Back to workspace
        </button>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="pricing-atlas-hero"
        >
          <p className="cracker-label">Access</p>
          <h1>
            One pass.<br />
            <span className="auth-brand-accent">Full Atlas.</span>
          </h1>
          <p className="pricing-atlas-hero-lead">
            No carousel of tiers — a single 30-day private lane with the same engine operators use on the deck.
            Pay once, deploy, and run.
          </p>
          <div className="pricing-atlas-pill-row">
            <span className="pricing-atlas-pill">Semi-dedicated compute</span>
            <span className="pricing-atlas-pill">10 Gbps</span>
            <span className="pricing-atlas-pill">English workspace</span>
          </div>
        </motion.section>

        <motion.aside
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="pricing-atlas-offer"
        >
          <p className="cracker-label mb-1">{plan.name}</p>
          <div className="pricing-atlas-price">
            600<span>€</span>
          </div>
          <p className="pricing-atlas-period">{plan.period}</p>
          <p className="text-xs text-[#737373] mt-2 leading-relaxed">{plan.description}</p>

          <div className="pricing-atlas-modules">
            <p className="pricing-atlas-modules-title">Add-ons available after login</p>
            <div className="pricing-atlas-modules-list">
              {['Harvest', 'Relay', 'Hygiene', 'Pay Intel'].map((m) => (
                <span key={m} className="pricing-atlas-module-chip">{m}</span>
              ))}
            </div>
          </div>

          <div className="pricing-atlas-features">
            {ATLAS_FEATURES.map((f, idx) =>
              f.head ? (
                <p key={idx} className="pricing-atlas-feature pricing-atlas-feature--head">
                  {f.name}
                </p>
              ) : (
                <div key={idx} className="pricing-atlas-feature">
                  <span className="pricing-atlas-feature-mark">✓</span>
                  <span>{f.name}</span>
                </div>
              )
            )}
          </div>

          <button type="button" onClick={handlePurchase} className="cracker-btn cracker-btn--gold w-full mt-4">
            Unlock Atlas Pass
          </button>
          <p className="text-[0.5625rem] text-[#525252] text-center mt-3 uppercase tracking-wider">
            Crypto only
          </p>
          <div className="pricing-atlas-crypto">
            <span className="pricing-crypto-tag">
              <BtcIcon /> {plan.cryptoPrice.BTC} BTC
            </span>
            <span className="pricing-crypto-tag">
              <EthIcon /> {plan.cryptoPrice.ETH} ETH
            </span>
            <span className="pricing-crypto-tag">
              <SolIcon /> {plan.cryptoPrice.SOL} SOL
            </span>
          </div>
        </motion.aside>

        <div className="pricing-atlas-trust">
          <span className="pricing-trust-badge">
            <span className="pricing-trust-dot" />
            No KYC
          </span>
          <span className="pricing-trust-badge">
            <span className="pricing-trust-dot" />
            No logs
          </span>
          <span className="pricing-trust-badge">
            <span className="pricing-trust-dot" />
            Instant provisioning
          </span>
        </div>
      </div>

      {showPaymentModal && (
        <PaymentModal
          plan={plan}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => {
            setShowPaymentModal(false);
            navigate('/');
          }}
        />
      )}
    </div>
  );
};

export default Pricing;
export { DEFAULT_PRICING_PLANS };
